// src/pages/Planes.tsx

import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'
import { Crown, Plus, Edit, Trash2, X, Save, CheckCircle } from 'lucide-react'
import { adminPlanService } from '../services/adminApiService'

export default function Planes() {
  const { isDark } = useTheme()
  
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)

  useEffect(() => {
    loadPlans()
  }, [])

  const loadPlans = async () => {
    try {
      setLoading(true)
      const response = await adminPlanService.getPlans()
      setPlans(response.plans || [])
    } catch (error) {
      console.error('Error loading plans:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePlanAction = async (action, planId, data) => {
    try {
      if (action === 'create') await adminPlanService.createPlan(data)
      else if (action === 'update') await adminPlanService.updatePlan(planId, data)
      else if (action === 'delete') await adminPlanService.deletePlan(planId)
      
      await loadPlans()
      setShowPlanModal(false)
      setEditingPlan(null)
    } catch (error) {
      console.error(`Error ${action} plan:`, error)
    }
  }

  const cardClass = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
  const textClass = isDark ? 'text-white' : 'text-gray-900'
  const mutedTextClass = isDark ? 'text-gray-400' : 'text-gray-600'
  const inputClass = isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${textClass} mb-2`}>
              Planes de Suscripción
            </h1>
            <p className={`${mutedTextClass}`}>
              Gestiona los planes disponibles ({plans.length} planes)
            </p>
          </div>

          <button
            onClick={() => {
              setEditingPlan(null)
              setShowPlanModal(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
          >
            <Plus className="w-4 h-4" />
            Nuevo Plan
          </button>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`${cardClass} border rounded-xl p-6 relative group hover:shadow-lg transition-all`}
            >
              {/* Actions */}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                <button
                  onClick={() => {
                    setEditingPlan(plan)
                    setShowPlanModal(true)
                  }}
                  className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`¿Eliminar plan "${plan.name}"?`)) {
                      handlePlanAction('delete', plan.id)
                    }
                  }}
                  className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Plan Content */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-6 h-6 text-yellow-500" />
                  <h3 className={`text-xl font-bold ${textClass}`}>{plan.name}</h3>
                </div>
                
                {plan.is_popular && (
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mb-2">
                    Popular
                  </span>
                )}

                {plan.is_active ? (
                  <span className="inline-block ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full mb-2">
                    Activo
                  </span>
                ) : (
                  <span className="inline-block ml-2 px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full mb-2">
                    Inactivo
                  </span>
                )}
              </div>

              <p className={`${mutedTextClass} text-sm mb-4`}>
                {plan.description}
              </p>

              {/* Pricing */}
              <div className="mb-6">
                <div className={`text-3xl font-bold ${textClass} mb-1`}>
                  ${plan.price}
                  <span className={`text-sm font-normal ${mutedTextClass}`}>/mes</span>
                </div>
                {plan.price_annual && (
                  <div className={`text-sm ${mutedTextClass}`}>
                    o ${plan.price_annual}/año
                  </div>
                )}
              </div>

              {/* Features */}
              {plan.features && Array.isArray(plan.features) && plan.features.length > 0 && (
                <ul className="space-y-2 mb-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className={`flex items-start gap-2 text-sm ${mutedTextClass}`}>
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Trial */}
              {plan.trial_days > 0 && (
                <div className={`text-xs ${mutedTextClass} mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  🎁 {plan.trial_days} días de prueba gratis
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Modal */}
        {showPlanModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${cardClass} rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
              <div className="flex justify-between items-center mb-6">
                <h3 className={`text-lg font-semibold ${textClass}`}>
                  {editingPlan ? 'Editar Plan' : 'Nuevo Plan'}
                </h3>
                <button onClick={() => setShowPlanModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.target)
                const features = formData.get('features').split('\n').filter(f => f.trim())
                
                const planData = {
                  plan_code: formData.get('plan_code'),
                  name: formData.get('name'),
                  description: formData.get('description'),
                  price_monthly: parseFloat(formData.get('price_monthly')),
                  price_annual: parseFloat(formData.get('price_annual')) || null,
                  currency: formData.get('currency') || 'USD',
                  features: features,
                  is_popular: formData.get('is_popular') === 'on',
                  is_active: formData.get('is_active') === 'on',
                  trial_days: parseInt(formData.get('trial_days')) || 0
                }

                handlePlanAction(editingPlan ? 'update' : 'create', editingPlan?.id, planData)
              }}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Código del Plan *
                    </label>
                    <input
                      type="text"
                      name="plan_code"
                      defaultValue={editingPlan?.plan_code}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                      placeholder="profesional"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Nombre *
                    </label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={editingPlan?.name}
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                      placeholder="Plan Profesional"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium ${textClass} mb-2`}>
                    Descripción
                  </label>
                  <textarea
                    name="description"
                    defaultValue={editingPlan?.description}
                    rows={2}
                    className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Precio Mensual *
                    </label>
                    <input
                      type="number"
                      name="price_monthly"
                      defaultValue={editingPlan?.price_monthly}
                      step="0.01"
                      required
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Precio Anual
                    </label>
                    <input
                      type="number"
                      name="price_annual"
                      defaultValue={editingPlan?.price_annual}
                      step="0.01"
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>
                      Días de Prueba
                    </label>
                    <input
                      type="number"
                      name="trial_days"
                      defaultValue={editingPlan?.trial_days || 0}
                      className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium ${textClass} mb-2`}>
                    Características (una por línea)
                  </label>
                  <textarea
                    name="features"
                    defaultValue={editingPlan?.features?.join('\n')}
                    rows={4}
                    className={`w-full px-3 py-2 rounded-lg border ${inputClass}`}
                    placeholder="Consultas ilimitadas&#10;Soporte prioritario&#10;Documentación especializada"
                  />
                </div>

                <div className="flex gap-6 mb-6">
                  <label className={`flex items-center gap-2 ${textClass}`}>
                    <input
                      type="checkbox"
                      name="is_popular"
                      defaultChecked={editingPlan?.is_popular}
                      className="rounded"
                    />
                    Plan Popular
                  </label>

                  <label className={`flex items-center gap-2 ${textClass}`}>
                    <input
                      type="checkbox"
                      name="is_active"
                      defaultChecked={editingPlan?.is_active !== false}
                      className="rounded"
                    />
                    Activo
                  </label>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowPlanModal(false)}
                    className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="w-4 h-4" />
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}